/**
 * Timetable Service
 * Handles all timetable-related operations including class schedules, elective subjects, and teacher assignments
 */

import { supabaseAdmin } from '../../supabase.js';

class TimetableService {
  constructor() {
    this.client = supabaseAdmin;
    this.admin = supabaseAdmin;
  }

  /**
   * Get all classes with their basic information
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of class objects
   */
  async getClasses(tenantId = 'tn_01') {
    try {
      // Try to get real class data
      try {
        const { data, error } = await this.client
          .from('saas_classes')
          .select(`
            id,
            class_id,
            name,
            offered_subjects,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('class_id', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_classes table not found, generating mock data');
      }

      // Fallback: Generate mock class data
      return this.generateMockClasses();
    } catch (error) {
      console.error('Error fetching classes:', error);
      // Return mock data as fallback
      return this.generateMockClasses();
    }
  }

  /**
   * Get students for a specific class
   * @param {string} classId - Class identifier
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of student objects
   */
  async getStudentsByClass(classId, tenantId = 'tn_01') {
    try {
      const { data, error } = await this.client
        .from('saas_students')
        .select(`
          id,
          student_name,
          student_id,
          class_id,
          tenant_id,
          elective_subjects,
          created_at,
          updated_at
        `)
        .eq('class_id', classId)
        .eq('tenant_id', tenantId)
        .order('student_id', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching students by class:', error);
      throw error;
    }
  }

  /**
   * Get all students with their class information
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of student objects with class info
   */
  async getAllStudents(tenantId = 'tn_01') {
    try {
      // Try to get real student data
      try {
        const { data, error } = await this.client
          .from('saas_students')
          .select(`
            id,
            student_name,
            student_id,
            class_id,
            tenant_id,
            elective_subjects,
            saas_classes!inner(
              id,
              class_id,
              name
            )
          `)
          .eq('tenant_id', tenantId)
          .order('student_id', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_students table not found, generating mock data');
      }

      // Fallback: Generate mock student data
      return this.generateMockStudents();
    } catch (error) {
      console.error('Error fetching all students:', error);
      // Return mock data as fallback
      return this.generateMockStudents();
    }
  }

  /**
   * Get teachers with their subject assignments
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of teacher objects
   */
  async getTeachers(tenantId = 'tn_01') {
    try {
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      };
      
      const actualTenantId = tenantIdMap[tenantId];
      if (!actualTenantId) {
        console.error('Invalid tenant ID:', tenantId);
        return this.generateMockTeachers();
      }

      // Try to get real teacher data
      try {
        const { data, error } = await this.client
          .from('saas_teachers')
          .select(`
            id,
            name,
            teacher_id,
            teacher_no,
            subject_specialty,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', actualTenantId)
          .order('teacher_no', { ascending: true });

        if (!error && data && data.length > 0) {
          console.log(`Successfully fetched ${data.length} teachers from saas_teachers table`);
          
          // Transform the data to match expected format
          const transformedData = data.map(teacher => ({
            id: teacher.id,
            teacher_name: teacher.name,
            teacher_id: teacher.teacher_id,
            teacher_no: teacher.teacher_no,
            subjects: teacher.subject_specialty,
            tenant_id: teacher.tenant_id,
            created_at: teacher.created_at,
            updated_at: teacher.updated_at
          }));
          
          return transformedData;
        }
      } catch (tableError) {
        console.log('saas_teachers table not found, generating mock data');
      }

      // Fallback: Generate mock teacher data
      return this.generateMockTeachers();
    } catch (error) {
      console.error('Error fetching teachers:', error);
      // Return mock data as fallback
      return this.generateMockTeachers();
    }
  }

  /**
   * Map UI class ID to database class ID
   * @param {string} uiClassId - UI class identifier (e.g., "中1A", "1A", "class_1A")
   * @returns {string} Database class identifier (e.g., "J1A", "S4A")
   */
  mapUIClassIdToDatabaseId(uiClassId) {
    if (!uiClassId) return uiClassId;
    
    // Handle different UI formats
    let normalizedId = uiClassId;
    
    // Remove "中" prefix if present
    if (normalizedId.startsWith('中')) {
      normalizedId = normalizedId.substring(1);
    }
    
    // Handle "class_X" format (e.g., "class_1A" -> "1A")
    if (normalizedId.startsWith('class_')) {
      normalizedId = normalizedId.substring(6); // Remove "class_" prefix
    }
    
    // Handle formats like "1A", "2B", etc.
    if (/^[1-6][A-D]$/.test(normalizedId)) {
      const form = normalizedId.charAt(0);
      const section = normalizedId.charAt(1);
      
      if (form >= 1 && form <= 3) {
        return `J${form}${section}`;
      } else if (form >= 4 && form <= 6) {
        return `S${form}${section}`;
      }
    }
    
    // If already in database format (J1A, S4A, etc.), return as is
    if (/^[JS][1-6][A-D]$/.test(uiClassId)) {
      return uiClassId;
    }
    
    // Default fallback
    console.warn(`Could not map UI class ID "${uiClassId}" to database format, using as is`);
    return uiClassId;
  }

  /**
   * Get timetable data for a specific class
   * @param {string} classId - Class identifier (UI format like "中1A" or database format like "J1A")
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Timetable data object
   */
  async getClassTimetable(classId, tenantId = 'tn_01') {
    try {
      // Map UI class ID to database class ID
      const dbClassId = this.mapUIClassIdToDatabaseId(classId);
      console.log(`Mapping UI class ID "${classId}" to database class ID "${dbClassId}"`);
      
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      };
      
      const actualTenantId = tenantIdMap[tenantId];
      if (!actualTenantId) {
        console.error('Invalid tenant ID:', tenantId);
        return this.generateMockTimetable(classId);
      }

      // Try to get real timetable data from saas_timetables table
        const { data, error } = await this.client
          .from('saas_timetables')
          .select(`
            id,
            class_id,
            day_of_week,
            time_slot,
            subject,
            teacher_id,
          teacher_name,
            classroom,
            subject_type,
            elective_group,
          period_number,
          notes
        `)
        .eq('class_id', dbClassId)
        .eq('tenant_id', actualTenantId)
          .order('day_of_week', { ascending: true })
        .order('period_number', { ascending: true });

        if (!error && data && data.length > 0) {
        console.log(`Found ${data.length} timetable entries for class ${dbClassId} (mapped from ${classId})`);
          return this.transformTimetableData(data);
      } else {
        console.log(`No timetable data found for class ${dbClassId} (mapped from ${classId}), using mock data`);
        console.log('Error details:', error);
      }

      // Fallback: Generate realistic mock timetable data
      return this.generateMockTimetable(classId);
    } catch (error) {
      console.error('Error fetching class timetable:', error);
      // Return mock data as fallback
      return this.generateMockTimetable(classId);
    }
  }

  /**
   * Get timetable data for a specific teacher
   * @param {string} teacherId - Teacher identifier
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Teacher timetable data object
   */
  async getTeacherTimetable(teacherId, tenantId = 'tn_01') {
    try {
      console.log(`🎯 Fetching teacher timetable for teacher ID: ${teacherId}`);
      console.log(`🏢 Using tenant ID: ${tenantId}`);
      console.log(`🔍 Teacher ID type: ${typeof teacherId}`);
      
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      };
      
      const actualTenantId = tenantIdMap[tenantId];
      if (!actualTenantId) {
        console.error('❌ Invalid tenant ID:', tenantId);
        return this.getEmptyTimetable();
      }
      
      console.log(`🔍 Searching for teacher ${teacherId} in tenant ${actualTenantId}`);
      
      // First, let's check if teacherId is a UUID or a string
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(teacherId);
      console.log(`🔍 Is teacherId a UUID? ${isUUID}`);
      
      // If it's a UUID, we need to find the corresponding teacher_id in saas_teachers table
      let actualTeacherId = teacherId;
      if (isUUID) {
        console.log(`🔍 Teacher ID is UUID, looking up teacher_id in saas_teachers table...`);
        const { data: teacherData, error: teacherError } = await this.client
          .from('saas_teachers')
          .select('teacher_id, name')
          .eq('id', teacherId)
          .eq('tenant_id', actualTenantId)
          .single();
        
        if (teacherError || !teacherData) {
          console.error('❌ Teacher not found in saas_teachers table:', teacherError);
          return this.getEmptyTimetable();
        } else {
          actualTeacherId = teacherData.teacher_id;
          console.log(`✅ Found teacher by UUID: ${teacherData.name} (${actualTeacherId})`);
        }
      }

      // Try to get real teacher timetable data from saas_teacher_timetables table
      console.log(`🔍 Querying saas_teacher_timetables with teacher_id: ${actualTeacherId}`);
      const { data, error } = await this.client
        .from('saas_teacher_timetables')
        .select(`
          id,
          teacher_id,
          teacher_name,
          day_of_week,
          time_slot,
          period_number,
          period_sequence,
          period_node,
          subject,
          class_id,
          classroom,
          week_type,
          subject_type,
          elective_group,
          notes
        `)
        .eq('teacher_id', actualTeacherId)
        .eq('tenant_id', actualTenantId)
        .order('day_of_week', { ascending: true })
        .order('period_number', { ascending: true });

      if (error) {
        console.error('❌ Error fetching teacher timetable from Supabase:', error);
        console.log('🔄 Falling back to mock data...');
        return this.getEmptyTimetable();
      }

      if (!data || data.length === 0) {
        console.log(`⚠️ No teacher timetable data found for teacher ${actualTeacherId} in saas_teacher_timetables table`);
        
        // Let's check what teacher IDs actually exist in the database
        console.log('🔍 Checking what teacher IDs exist in the database...');
        const { data: allTeachers, error: allTeachersError } = await this.client
          .from('saas_teacher_timetables')
          .select('teacher_id, teacher_name')
          .eq('tenant_id', actualTenantId)
          .limit(10);
        
        if (!allTeachersError && allTeachers && allTeachers.length > 0) {
          console.log('📋 Available teacher IDs in database:', allTeachers.map(t => ({ id: t.teacher_id, name: t.teacher_name })));
        } else {
          console.log('❌ No teachers found in saas_teacher_timetables table');
        }
        
        console.log('🔄 Falling back to mock data...');
        return this.getEmptyTimetable();
      }

      console.log(`✅ Found ${data.length} teacher timetable entries for teacher ${actualTeacherId} from Supabase`);
      console.log('📊 Sample data entry:', data[0]);
      
      // Transform the data into the expected format
      const transformedData = this.transformTeacherTimetableData(data, teacherId);
      console.log('🔄 Transformed data sample:', transformedData.monday?.[0]);
      return transformedData;
      
    } catch (error) {
      console.error('Error fetching teacher timetable:', error);
      // Return mock data as fallback
      return this.getEmptyTimetable();
    }
  }

  /**
   * Transform teacher timetable data from database format to UI format
   * @param {Array} data - Raw database data
   * @param {string} teacherId - Teacher ID
   * @returns {Object} Transformed timetable data
   */
  transformTeacherTimetableData(data, teacherId) {
    // Create 15-period structure (period_sequence 1-15)
    const timetable = {
      monday: new Array(15).fill(null),
      tuesday: new Array(15).fill(null),
      wednesday: new Array(15).fill(null),
      thursday: new Array(15).fill(null),
      friday: new Array(15).fill(null)
    };

    // Group data by day
    const dayMapping = {
      'Monday': 'monday',
      'Tuesday': 'tuesday', 
      'Wednesday': 'wednesday',
      'Thursday': 'thursday',
      'Friday': 'friday'
    };

    data.forEach(entry => {
      const dayKey = dayMapping[entry.day_of_week];
      if (!dayKey) return;

      // Debug logging for period data
      if (entry.period_sequence || entry.period_node) {
        console.log('📅 Processing period data:', {
          day: entry.day_of_week,
          period_sequence: entry.period_sequence,
          period_node: entry.period_node,
          period_number: entry.period_number,
          subject: entry.subject
        });
      }

      const scheduleEntry = {
        subject: entry.subject,
        teacher: entry.teacher_name,
        classroom: entry.classroom,
        class_id: entry.class_id,
        type: entry.subject_type || 'core',
        elective_group: entry.elective_group,
        week_type: entry.week_type || 'regular',
        notes: entry.notes || '',
        // Include period_sequence and period_node from Supabase data
        period_sequence: entry.period_sequence,
        period_node: entry.period_node,
        period_number: entry.period_number,
        time_slot: entry.time_slot
      };

      // Place entry at the correct period_sequence index (period_sequence - 1 for 0-based array)
      if (entry.period_sequence && entry.period_sequence >= 1 && entry.period_sequence <= 15) {
        const arrayIndex = entry.period_sequence - 1; // Convert 1-based period_sequence to 0-based array index
        console.log(`📊 Placing ${entry.subject} at period_sequence ${entry.period_sequence} (array index ${arrayIndex}) for ${dayKey}`);
        timetable[dayKey][arrayIndex] = scheduleEntry;
      }
    });

    // Data is already organized by period_sequence, no sorting needed

    return timetable;
  }

  /**
   * Generate realistic mock teacher timetable data
   * @param {string} teacherId - Teacher identifier
   * @returns {Object} Mock teacher timetable data
   */
  getEmptyTimetable() {
    return {
      monday: new Array(15).fill(null),
      tuesday: new Array(15).fill(null),
      wednesday: new Array(15).fill(null),
      thursday: new Array(15).fill(null),
      friday: new Array(15).fill(null)
    };
  }

  generateMockTeacherTimetable(teacherId) {
    console.log(`Generating mock teacher timetable for teacher: ${teacherId}`);
    
    // Time slots
    const timeSlots = [
      '8:00-8:40', '8:40-9:20', '9:20-10:00', '10:20-11:00', 
      '11:00-11:40', '11:40-12:20', '1:20-2:00', '2:00-2:40'
    ];
    
    // Days of the week
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    // Generate timetable
    const timetable = {};
    
    days.forEach((day, dayIndex) => {
      timetable[day] = timeSlots.map((timeSlot, slotIndex) => {
        // Skip some slots for breaks
        if (slotIndex === 3) {
          return {
            time: timeSlot,
            subject: '小息',
            teacher: '',
            classroom: '',
            class_id: '',
            type: 'break'
          };
        }
        
        // Generate different subjects for different time slots
        const subjects = ['中文', '英文', '數學', '通識教育', '物理', '化學', '生物', '歷史'];
        const classrooms = ['A101', 'A102', 'A103', 'A104', 'A105', 'A106', 'A107', 'A108'];
        const classes = ['J1A', 'J1B', 'J1C', 'J1D', 'J2A', 'J2B', 'J2C', 'J2D'];
        
        const subjectIndex = (dayIndex + slotIndex) % subjects.length;
        const classroomIndex = (dayIndex + slotIndex) % classrooms.length;
        const classIndex = (dayIndex + slotIndex) % classes.length;
        
        return {
          time: timeSlot,
          subject: subjects[subjectIndex],
          teacher: '老師',
          classroom: classrooms[classroomIndex],
          class_id: classes[classIndex],
          type: 'core'
        };
      });
    });
    
    return timetable;
  }

  /**
   * Generate realistic mock timetable data for a class
   * @param {string} classId - Class identifier
   * @returns {Object} Mock timetable data
   */
  generateMockTimetable(classId) {
    // Determine class level from class ID
    const classLevel = this.extractClassLevel(classId);
    
    // For Form 1, 2, and 3 classes, use real timetable data
    if (classLevel >= 1 && classLevel <= 3) {
      return this.generateForm123Timetable(classId);
    }
    
    // For Form 4, 5, and 6 classes, use real timetable data
    if (classLevel >= 4) {
      return this.generateForm6Timetable(classId);
    }
    
    // Define subjects based on class level
    const coreSubjects = ['中文', '英文', '數學', '通識教育'];
    const scienceSubjects = ['物理', '化學', '生物'];
    const humanitiesSubjects = ['歷史', '地理', '經濟'];
    
    // Time slots
    const timeSlots = [
      '8:10-8:45', '8:45-9:20', '9:20-9:55', '10:15-10:50', 
      '10:50-11:25', '11:30-12:05', '12:05-12:40', '1:50-2:25', 
      '2:25-3:00', '3:00-3:35'
    ];
    
    // Days of the week
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    // Generate timetable
    const timetable = {};
    
    days.forEach((day, dayIndex) => {
      timetable[day] = timeSlots.map((timeSlot, slotIndex) => {
        // Skip some slots for breaks
        if (slotIndex === 3 || slotIndex === 6) {
          return {
            time: timeSlot,
            subject: slotIndex === 3 ? '小息' : '午膳',
            classroom: '',
            teacher: '',
            type: 'break'
          };
        }
        
        // Determine subject based on day and time
        let subject = '';
        let teacher = '';
        let classroom = '';
        let type = 'core';
        
          // Lower forms - core subjects only
          const subjectIndex = (dayIndex * 8 + slotIndex) % 6;
          if (subjectIndex < 4) {
            subject = coreSubjects[subjectIndex % coreSubjects.length];
            teacher = this.getMockTeacher(subject);
            classroom = this.getMockClassroom(subject);
          } else {
            subject = scienceSubjects[subjectIndex % scienceSubjects.length];
            teacher = this.getMockTeacher(subject);
            classroom = this.getMockClassroom(subject);
        }
        
        return {
          time: timeSlot,
          subject,
          classroom,
          teacher,
          type
        };
      });
    });
    
    return timetable;
  }
  
  /**
   * Generate Form 4, 5, and 6 timetable with real data structure
   * @param {string} classId - Class identifier (S4A-S4D, S5A-S5D, S6A-S6D)
   * @returns {Object} Form 4/5/6 timetable data
   */
  generateForm6Timetable(classId) {
    const form4_5_6Timetables = {
      'S6A': {
        classTeacher: '楊國勇',
        viceTeacher: 'Naz',
        classroom: '417',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: 'Naz', classroom: '417', type: 'special' },
            { time: '8:45-9:20', subject: '數學', teacher: '徐隆堅', classroom: '417', type: 'core' },
            { time: '9:20-9:55', subject: '數學', teacher: '徐隆堅', classroom: '417', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: 'S6跨班自主學習節', teacher: '楊國勇/徐文超/吳曉敏/范伊雲/郭豫婷', classroom: '417/433/207/208/509/Hall', type: 'special' },
            { time: '11:30-12:05', subject: '英國語文', teacher: '陳柏輝/梁燕詩', classroom: '417/433', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '中國語文', teacher: '楊國勇', classroom: '417', type: 'core' },
            { time: '2:25-3:00', subject: 'X2', teacher: 'Multiple', classroom: 'Multiple', type: 'elective', electives: [
              { subject: '生物', teacher: '鄒永新', classroom: '310' },
              { subject: '設應', teacher: '鄭明忠', classroom: '202' },
              { subject: '經濟', teacher: '李志堅', classroom: '208' },
              { subject: '生技', teacher: '黃添文/陳權福', classroom: '511/G12' },
              { subject: '物理', teacher: '黎志偉', classroom: '308' },
              { subject: '旅款', teacher: '吳靜儀', classroom: '417' }
            ]},
            { time: '3:00-3:35', subject: 'X2', teacher: 'Multiple', classroom: 'Multiple', type: 'elective', electives: [
              { subject: '生物', teacher: '鄒永新', classroom: '310' },
              { subject: '設實', teacher: '鄭明忠', classroom: '108' },
              { subject: '經濟', teacher: '李志堅', classroom: '208' },
              { subject: '生技', teacher: '黃添文/陳權福', classroom: '511' },
              { subject: '物理', teacher: '黎志偉', classroom: '308' },
              { subject: '旅款', teacher: '吳靜儀', classroom: '417' }
            ]}
          ],
          tuesday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '陳柏輝/梁燕詩', classroom: '417/433', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '陳柏輝/梁燕詩', classroom: '417/433', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '楊國勇', classroom: '417', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: 'X1', teacher: 'Multiple', classroom: 'Multiple', type: 'elective', electives: [
              { subject: '會計', teacher: '鍾麗綺', classroom: '417' },
              { subject: '管理', teacher: '施上海', classroom: '433' },
              { subject: '化學', teacher: '蔡美玉', classroom: '314' },
              { subject: '資通', teacher: '鄒志良', classroom: '511' },
              { subject: '體育', teacher: '孫瑞強', classroom: '207' },
              { subject: '視藝', teacher: '陳凱怡', classroom: '103' }
            ]},
            { time: '11:30-12:05', subject: 'X2', teacher: 'Multiple', classroom: 'Multiple', type: 'elective', electives: [
              { subject: '生物', teacher: '鄒永新', classroom: '310' },
              { subject: '設應', teacher: '鄭明忠', classroom: '202' },
              { subject: '經濟', teacher: '李志堅', classroom: '208' },
              { subject: '生技', teacher: '黃添文/陳權福', classroom: '511' },
              { subject: '物理', teacher: '黎志偉', classroom: '308' },
              { subject: '旅款', teacher: '吳靜儀', classroom: '417' }
            ]},
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '6AB跨班自主學習節', teacher: '徐隆堅/鄭振壽', classroom: '417/433/Hall', type: 'special' },
            { time: '2:25-3:00', subject: '體育', teacher: '陳雕鳳/孫瑞強', classroom: 'SG1', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '陳雕鳳/孫瑞強', classroom: 'SG1', type: 'core' }
          ]
          // Continue with other days for S6A...
        }
      },
      'S6B': {
        classTeacher: '鄧皓明',
        viceTeacher: '吳靜儀',
        classroom: '433',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: '吳靜儀', classroom: '433', type: 'special' },
            { time: '8:45-9:20', subject: '數學', teacher: '鄭振壽', classroom: '433', type: 'core' },
            { time: '9:20-9:55', subject: '數學', teacher: '鄭振壽', classroom: '433', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: 'S6跨班自主學習節', teacher: '楊國勇/徐文超/吳曉敏/范伊雲/郭豫婷', classroom: '417/433/207/208/509/Hall', type: 'special' },
            { time: '11:30-12:05', subject: '英國語文', teacher: '陳柏輝/梁燕詩', classroom: '417/433', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '公民與社會發展科', teacher: '鄧皓明', classroom: '433', type: 'core' },
            { time: '2:25-3:00', subject: 'X2', teacher: 'Multiple', classroom: 'Multiple', type: 'elective', electives: [
              { subject: '生物', teacher: '鄒永新', classroom: '310' },
              { subject: '設實', teacher: '鄭明忠', classroom: '108' },
              { subject: '經濟', teacher: '李志堅', classroom: '208' },
              { subject: '生技', teacher: '黃添文/陳權福', classroom: 'G12' },
              { subject: '物理', teacher: '黎志偉', classroom: '308' },
              { subject: '旅款', teacher: '吳靜儀', classroom: '417' }
            ]},
            { time: '3:00-3:35', subject: 'X2', teacher: 'Multiple', classroom: 'Multiple', type: 'elective', electives: [
              { subject: '生物', teacher: '鄒永新', classroom: '310' },
              { subject: '設實', teacher: '鄭明忠', classroom: '108' },
              { subject: '經濟', teacher: '李志堅', classroom: '208' },
              { subject: '生技', teacher: '黃添文/陳權福', classroom: 'G12' },
              { subject: '物理', teacher: '黎志偉', classroom: '308' },
              { subject: '旅款', teacher: '吳靜儀', classroom: '417' }
            ]}
          ]
          // Continue with other days for S6B...
        }
      },
      'S4A': {
        classTeacher: '徐隆堅',
        viceTeacher: '藍楚喬',
        classroom: '703',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: '藍楚喬', classroom: '703', type: 'special' },
            { time: '8:45-9:20', subject: 'S4跨班自主學習節', teacher: '楊國勇/黃鳳儀/梁俊賢/范伊雲/陳廣隆', classroom: '703/704/110/705/706/Hall', type: 'special' },
            { time: '9:20-9:55', subject: 'S4跨班自主學習節', teacher: '楊國勇/黃鳳儀/梁俊賢/范伊雲/陳廣隆', classroom: '703/704/110/705/706/Hall', type: 'special' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: 'X1', teacher: 'Multiple', classroom: 'Multiple', type: 'elective', electives: [
              { subject: '會計', teacher: '鍾麗綺', classroom: '703' },
              { subject: '管理', teacher: '余潔珍', classroom: '704' },
              { subject: '化學', teacher: '蔡美玉', classroom: '314' },
              { subject: '中史', teacher: '陸思敏', classroom: '706' },
              { subject: '資通', teacher: '鄒志良', classroom: '511' },
              { subject: '體育', teacher: '陳雕鳳', classroom: '705' },
              { subject: '視藝', teacher: '陳凱怡', classroom: '103' }
            ]},
            { time: '11:30-12:05', subject: '英國語文', teacher: '藍楚喬/任康哲/鄭潤章', classroom: '703/704/110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '公民與社會發展科', teacher: '蘇家輝', classroom: '703', type: 'core' },
            { time: '2:25-3:00', subject: '數學', teacher: '徐隆堅', classroom: '703', type: 'core' },
            { time: '3:00-3:35', subject: '數學', teacher: '徐隆堅', classroom: '703', type: 'core' }
          ]
        }
      }
      // Add S4B, S4C, S4D, S5A, S5B, S5C, S5D, S6C and S6D...
    };

    // Return the specific class timetable or default structure
    const classKey = classId.toUpperCase();
    if (form4_5_6Timetables[classKey]) {
      return form4_5_6Timetables[classKey].timetable;
    }

    // Fallback to basic Form 6 structure
    return this.generateBasicForm6Timetable(classId);
  }

  /**
   * Generate Form 1, 2, and 3 timetable with real data structure
   * @param {string} classId - Class identifier (J1A-J1D, J2A-J2D, J3A-J3D)
   * @returns {Object} Form 1/2/3 timetable data
   */
  generateForm123Timetable(classId) {
    const form123Timetables = {
      'J1A': {
        classTeacher: '陳廣隆',
        viceTeacher: '陳雕鳳',
        classroom: '110',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: '陳雕鳳', classroom: '110', type: 'special' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '2:25-3:00', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '陳雕鳳', classroom: 'SG1', type: 'core' }
          ],
          tuesday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ],
          wednesday: [
            { time: '8:10-8:45', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '110', type: 'core' },
            { time: '3:00-3:35', subject: '音樂', teacher: '吳靜儀', classroom: '509', type: 'core' }
          ],
          thursday: [
            { time: '8:10-8:45', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '陳雕鳳', classroom: 'SG1', type: 'core' }
          ],
          friday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '陳廣隆', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '110', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ]
        }
      },
      'J1B': {
        classTeacher: '梁俊賢',
        viceTeacher: '孫瑞強',
        classroom: '705',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: '孫瑞強', classroom: '705', type: 'special' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '11:30-12:05', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '2:25-3:00', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '孫瑞強', classroom: 'SG1', type: 'core' }
          ],
          tuesday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ],
          wednesday: [
            { time: '8:10-8:45', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '8:45-9:20', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '705', type: 'core' },
            { time: '3:00-3:35', subject: '音樂', teacher: '吳靜儀', classroom: '509', type: 'core' }
          ],
          thursday: [
            { time: '8:10-8:45', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '孫瑞強', classroom: 'SG1', type: 'core' }
          ],
          friday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '任康哲', classroom: '705', type: 'core' },
            { time: '9:20-9:55', subject: '數學', teacher: '黃鳳儀', classroom: '705', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '梁俊賢', classroom: '705', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '705', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ]
        }
      },
      'J1C': {
        classTeacher: '范伊雲',
        viceTeacher: '陳雕鳳',
        classroom: '704',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: '陳雕鳳', classroom: '704', type: 'special' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '11:30-12:05', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '2:25-3:00', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '陳雕鳳', classroom: 'SG1', type: 'core' }
          ],
          tuesday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ],
          wednesday: [
            { time: '8:10-8:45', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '8:45-9:20', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '704', type: 'core' },
            { time: '3:00-3:35', subject: '音樂', teacher: '吳靜儀', classroom: '509', type: 'core' }
          ],
          thursday: [
            { time: '8:10-8:45', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '陳雕鳳', classroom: 'SG1', type: 'core' }
          ],
          friday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '鄭潤章', classroom: '704', type: 'core' },
            { time: '9:20-9:55', subject: '數學', teacher: '黃鳳儀', classroom: '704', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '范伊雲', classroom: '704', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '704', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ]
        }
      },
      'J1D': {
        classTeacher: '楊國勇',
        viceTeacher: '孫瑞強',
        classroom: '706',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: '孫瑞強', classroom: '706', type: 'special' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '11:30-12:05', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '孫瑞強', classroom: 'SG1', type: 'core' }
          ],
          tuesday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ],
          wednesday: [
            { time: '8:10-8:45', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '8:45-9:20', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '706', type: 'core' },
            { time: '3:00-3:35', subject: '音樂', teacher: '吳靜儀', classroom: '509', type: 'core' }
          ],
          thursday: [
            { time: '8:10-8:45', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '孫瑞強', classroom: 'SG1', type: 'core' }
          ],
          friday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '任康哲', classroom: '706', type: 'core' },
            { time: '9:20-9:55', subject: '數學', teacher: '黃鳳儀', classroom: '706', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '楊國勇', classroom: '706', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '706', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ]
        }
      },
      'J2A': {
        classTeacher: '藍楚喬',
        viceTeacher: '陳雕鳳',
        classroom: '110',
        timetable: {
          monday: [
            { time: '8:10-8:45', subject: '閱讀節', teacher: '陳雕鳳', classroom: '110', type: 'special' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '2:25-3:00', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '陳雕鳳', classroom: 'SG1', type: 'core' }
          ],
          tuesday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ],
          wednesday: [
            { time: '8:10-8:45', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '110', type: 'core' },
            { time: '3:00-3:35', subject: '音樂', teacher: '吳靜儀', classroom: '509', type: 'core' }
          ],
          thursday: [
            { time: '8:10-8:45', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '2:25-3:00', subject: '科學', teacher: '蔡美玉', classroom: '314', type: 'core' },
            { time: '3:00-3:35', subject: '體育', teacher: '陳雕鳳', classroom: 'SG1', type: 'core' }
          ],
          friday: [
            { time: '8:10-8:45', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '8:45-9:20', subject: '英國語文', teacher: '鄭潤章', classroom: '110', type: 'core' },
            { time: '9:20-9:55', subject: '數學', teacher: '黃鳳儀', classroom: '110', type: 'core' },
            { time: '10:15-10:50', subject: '小息', teacher: '', classroom: '', type: 'break' },
            { time: '10:50-11:25', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '11:30-12:05', subject: '中國語文', teacher: '藍楚喬', classroom: '110', type: 'core' },
            { time: '12:05-12:40', subject: '午膳', teacher: '', classroom: '', type: 'break' },
            { time: '1:50-2:25', subject: '歷史', teacher: '陸思敏', classroom: '706', type: 'core' },
            { time: '2:25-3:00', subject: '地理', teacher: '蘇家輝', classroom: '110', type: 'core' },
            { time: '3:00-3:35', subject: '視藝', teacher: '陳凱怡', classroom: '103', type: 'core' }
          ]
        }
      }
      // Add remaining J2B-J2D, J3A-J3D timetables here...
    };

    // Return the specific class timetable or default structure
    const classKey = classId.toUpperCase();
    if (form123Timetables[classKey]) {
      return form123Timetables[classKey].timetable;
    }

    // Fallback to basic Form 1/2/3 structure
    return this.generateBasicForm123Timetable(classId);
  }

  /**
   * Generate basic Form 1, 2, and 3 timetable structure
   * @param {string} classId - Class identifier
   * @returns {Object} Basic Form 1/2/3 timetable
   */
  generateBasicForm123Timetable(classId) {
    const timeSlots = [
      '8:10-8:45', '8:45-9:20', '9:20-9:55', '10:15-10:50', 
      '10:50-11:25', '11:30-12:05', '12:05-12:40', '1:50-2:25', 
      '2:25-3:00', '3:00-3:35'
    ];
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const timetable = {};
    
    days.forEach(day => {
      timetable[day] = timeSlots.map((timeSlot, slotIndex) => {
        if (slotIndex === 3 || slotIndex === 6) {
          return {
            time: timeSlot,
            subject: slotIndex === 3 ? '小息' : '午膳',
            classroom: '',
            teacher: '',
            type: 'break'
          };
        }
        
        // Basic Form 1/2/3 structure
        const subjects = ['中文', '英文', '數學', '科學', '歷史', '地理', '體育', '視藝', '音樂'];
        const subject = subjects[slotIndex % subjects.length];
        
        return {
          time: timeSlot,
          subject,
          classroom: this.getMockClassroom(subject),
          teacher: this.getMockTeacher(subject),
          type: 'core'
        };
      });
    });
    
    return timetable;
  }

  /**
   * Generate basic Form 6 timetable structure
   * @param {string} classId - Class identifier
   * @returns {Object} Basic Form 6 timetable
   */
  generateBasicForm6Timetable(classId) {
    const timeSlots = [
      '8:10-8:45', '8:45-9:20', '9:20-9:55', '10:15-10:50', 
      '10:50-11:25', '11:30-12:05', '12:05-12:40', '1:50-2:25', 
      '2:25-3:00', '3:00-3:35'
    ];
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const timetable = {};
    
    days.forEach(day => {
      timetable[day] = timeSlots.map((timeSlot, slotIndex) => {
        if (slotIndex === 3 || slotIndex === 6) {
          return {
            time: timeSlot,
            subject: slotIndex === 3 ? '小息' : '午膳',
            classroom: '',
            teacher: '',
            type: 'break'
          };
        }
        
        // Basic Form 6 structure
        const subjects = ['中文', '英文', '數學', '公民與社會發展科', 'X1', 'X2'];
        const subject = subjects[slotIndex % subjects.length];
        
        return {
          time: timeSlot,
          subject,
          classroom: this.getMockClassroom(subject),
          teacher: this.getMockTeacher(subject),
          type: subject.includes('X') ? 'elective' : 'core'
        };
      });
    });
    
    return timetable;
  }
  
  /**
   * Extract class level from class ID
   * @param {string} classId - Class identifier (UI or database format)
   * @returns {number} Class level
   */
  extractClassLevel(classId) {
    // First map to database format to ensure consistent parsing
    const dbClassId = this.mapUIClassIdToDatabaseId(classId);
    
    if (dbClassId.includes('S4') || classId.includes('中四')) return 4;
    if (dbClassId.includes('S5') || classId.includes('中五')) return 5;
    if (dbClassId.includes('S6') || classId.includes('中六')) return 6;
    if (dbClassId.includes('J1') || classId.includes('中一')) return 1;
    if (dbClassId.includes('J2') || classId.includes('中二')) return 2;
    if (dbClassId.includes('J3') || classId.includes('中三')) return 3;
    
    // Handle numeric formats like "1A", "2B", etc.
    if (/^[1-6][A-D]$/.test(classId) || /^中[1-6][A-D]$/.test(classId)) {
      const match = classId.match(/中?([1-6])/);
      if (match) {
        return parseInt(match[1]);
      }
    }
    
    return 4; // Default to Form 4
  }
  
  /**
   * Get mock teacher name for a subject
   * @param {string} subject - Subject name
   * @returns {string} Teacher name
   */
  getMockTeacher(subject) {
    const teachers = {
      '中文': '王老師',
      '英文': '李老師',
      '數學': '張老師',
      '通識教育': '陳老師',
      '物理': '劉老師',
      '化學': '黃老師',
      '生物': '林老師',
      '歷史': '吳老師',
      '地理': '鄭老師',
      '經濟': '周老師',
      '數學延伸': '蔡老師'
    };
    return teachers[subject] || '老師';
  }
  
  /**
   * Get mock classroom for a subject
   * @param {string} subject - Subject name
   * @returns {string} Classroom
   */
  getMockClassroom(subject) {
    const classrooms = {
      '中文': 'A101',
      '英文': 'A102',
      '數學': 'A103',
      '通識教育': 'A104',
      '物理': 'B201',
      '化學': 'B202',
      '生物': 'B203',
      '歷史': 'C301',
      '地理': 'C302',
      '經濟': 'C303',
      '數學延伸': 'A105'
    };
    return classrooms[subject] || 'A101';
  }

  /**
   * Transform raw timetable data into component-friendly format
   * @param {Array} rawData - Raw timetable data from database
   * @returns {Object} Transformed timetable data
   */
  transformTimetableData(rawData) {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    // Define time slots in both formats (database format and display format)
    const timeSlotMappings = [
      { db: '08:10-08:45', display: '8:10-8:45' },
      { db: '08:45-09:20', display: '8:45-9:20' },
      { db: '09:20-09:55', display: '9:20-9:55' },
      { db: '10:15-10:50', display: '10:15-10:50' },
      { db: '10:50-11:25', display: '10:50-11:25' },
      { db: '11:30-12:05', display: '11:30-12:05' },
      { db: '12:05-12:40', display: '12:05-12:40' },
      { db: '13:50-14:25', display: '1:50-2:25' },
      { db: '14:25-15:00', display: '2:25-3:00' },
      { db: '15:00-15:35', display: '3:00-3:35' }
    ];

    const timetable = {};
    
    // Initialize all days and time slots
    days.forEach(day => {
      timetable[day] = timeSlotMappings.map(slot => ({
        time: slot.display,
        subject: '',
        classroom: '',
        teacher: '',
        type: 'core'
      }));
    });

    // Fill in the actual data
    rawData.forEach(entry => {
      const dayIndex = days.indexOf(entry.day_of_week.toLowerCase());
      
      // Find the matching time slot (try both database and display formats)
      let timeIndex = -1;
      for (let i = 0; i < timeSlotMappings.length; i++) {
        if (entry.time_slot === timeSlotMappings[i].db || 
            entry.time_slot === timeSlotMappings[i].display) {
          timeIndex = i;
          break;
        }
      }
      
      if (dayIndex !== -1 && timeIndex !== -1) {
        timetable[days[dayIndex]][timeIndex] = {
          time: timeSlotMappings[timeIndex].display,
          subject: entry.subject || '',
          classroom: entry.classroom || '',
          teacher: entry.teacher_name || '',
          type: entry.subject_type || 'core',
          electiveGroup: entry.elective_group,
          notes: entry.notes
        };
      } else {
        console.warn(`Could not map timetable entry: day=${entry.day_of_week}, time=${entry.time_slot}`);
      }
    });

    return timetable;
  }

  /**
   * Update student elective subjects
   * @param {string} studentId - Student identifier
   * @param {Array} electives - Array of elective subjects
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Updated student object
   */
  async updateStudentElectives(studentId, electives, tenantId = 'tn_01') {
    try {
      const { data, error } = await this.client
        .from('saas_students')
        .update({
          elective_subjects: electives,
          updated_at: new Date().toISOString()
        })
        .eq('id', studentId)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating student electives:', error);
      throw error;
    }
  }

  /**
   * Get elective subject options
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Elective subject options
   */
  async getElectiveOptions(tenantId = 'tn_01') {
    try {
      const { data, error } = await this.client
        .from('saas_elective_options')
        .select(`
          id,
          group_name,
          subjects,
          description,
          tenant_id
        `)
        .eq('tenant_id', tenantId)
        .order('group_name', { ascending: true });

      if (error) throw error;

      // Transform into the format expected by the component
      const options = {};
      (data || []).forEach(option => {
        options[option.group_name] = {
          subjects: option.subjects,
          description: option.description
        };
      });

      return options;
    } catch (error) {
      console.error('Error fetching elective options:', error);
      // Return default options if database doesn't have them
      return {
        'X1': {
          subjects: ['生物', '化學', '物理', '歷史', '地理'],
          description: '學生需從生物、化學、物理、歷史、地理中選擇一科'
        },
        'X2': {
          subjects: ['生物', '化學', '物理', '歷史', '數學延伸'],
          description: '學生需從生物、化學、物理、歷史、數學延伸中選擇一科'
        }
      };
    }
  }

  /**
   * Create or update timetable entry
   * @param {Object} timetableEntry - Timetable entry data
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Created/updated timetable entry
   */
  async saveTimetableEntry(timetableEntry, tenantId = 'tn_01') {
    try {
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      };
      
      const actualTenantId = tenantIdMap[tenantId];
      if (!actualTenantId) {
        throw new Error('Invalid tenant ID: ' + tenantId);
      }

      // Map class ID to database format if needed
      const entryData = {
          ...timetableEntry,
        class_id: this.mapUIClassIdToDatabaseId(timetableEntry.class_id),
        tenant_id: actualTenantId,
          updated_at: new Date().toISOString()
      };

      const { data, error } = await this.client
        .from('saas_timetables')
        .upsert(entryData)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error saving timetable entry:', error);
      throw error;
    }
  }

  /**
   * Delete timetable entry
   * @param {string} entryId - Timetable entry identifier
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<boolean>} Success status
   */
  async deleteTimetableEntry(entryId, tenantId = 'tn_01') {
    try {
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      };
      
      const actualTenantId = tenantIdMap[tenantId];
      if (!actualTenantId) {
        throw new Error('Invalid tenant ID: ' + tenantId);
      }

      const { error } = await this.client
        .from('saas_timetables')
        .delete()
        .eq('id', entryId)
        .eq('tenant_id', actualTenantId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting timetable entry:', error);
      throw error;
    }
  }

  /**
   * Get timetable statistics
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Timetable statistics
   */
  async getTimetableStats(tenantId = 'tn_01') {
    try {
      const [classesResult, teachersResult, studentsResult] = await Promise.all([
        this.getClasses(tenantId),
        this.getTeachers(tenantId),
        this.getAllStudents(tenantId)
      ]);

      const electiveClasses = classesResult.filter(cls => 
        cls.class_level >= 6 // Form 6 classes typically have electives
      );

      const electiveStudents = studentsResult.filter(student => 
        student.elective_subjects && student.elective_subjects.length > 0
      );

      return {
        totalClasses: classesResult.length,
        totalTeachers: teachersResult.length,
        totalStudents: studentsResult.length,
        electiveClasses: electiveClasses.length,
        electiveStudents: electiveStudents.length,
        classLevels: [...new Set(classesResult.map(cls => cls.class_level))].sort()
      };
    } catch (error) {
      console.error('Error fetching timetable stats:', error);
      throw error;
    }
  }

  /**
   * Generate mock class data
   * @returns {Array} Array of mock class objects
   */
  generateMockClasses() {
    const classes = [];
    const classLevels = [1, 2, 3, 4, 5, 6];
    const sections = ['A', 'B', 'C', 'D'];
    
    // Mock teacher data for assignments
    const mockTeachers = [
      { name: '鄒永新', subject: 'Biology' },
      { name: '李志堅', subject: 'Economics' },
      { name: '陳雕鳳', subject: 'Physical Education' },
      { name: 'FIONA WANGARI', subject: 'To be assigned' },
      { name: '鄒志良', subject: 'ICT' }
    ];
    
    classLevels.forEach(level => {
      sections.forEach(section => {
        const classId = `class_${level}${section}`;
        const className = level <= 3 ? `J${level}${section}` : `S${level-3}${section}`;
        
        // Assign class master for some classes
        let classMasterName = '未安排';
        let classMasterSubject = null;
        if (className === 'J1C') {
          classMasterName = '鄒永新';
          classMasterSubject = 'Biology';
        } else if (className === 'J2A') {
          classMasterName = '李志堅';
          classMasterSubject = 'Economics';
        }
        
        classes.push({
          id: classId,
          class_name: className,
          class_level: level,
          class_section: section,
          academic_year: '2024-2025',
          tenant_id: 'tn_01',
          student_count: 25,
          class_master_name: classMasterName,
          class_master_subject: classMasterSubject,
          vice_class_master_name: '未安排',
          subject_teachers: mockTeachers,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      });
    });
    
    return classes;
  }
  
  /**
   * Generate mock student data
   * @returns {Array} Array of mock student objects
   */
  generateMockStudents() {
    const students = [];
    const classLevels = [1, 2, 3, 4, 5, 6];
    const sections = ['A', 'B', 'C', 'D'];
    const studentNames = [
      '陳小明XXX', '李美玲', '張志偉', '王雅婷', '劉家豪', 
      '黃思穎', '林俊傑', '吳佳欣', '郭志強', '謝雅文', 
      '陳志偉', '李雅婷', '張家豪', '王美玲', '劉俊傑'
    ];
    
    classLevels.forEach(level => {
      sections.forEach(section => {
        const classId = `class_${level}${section}`;
        const className = level <= 3 ? `J${level}${section}` : `S${level-3}${section}`;
        
        // Generate 15 students per class (like in the 4th image)
        for (let i = 1; i <= 15; i++) {
          const nameIndex = (level * 4 + section.charCodeAt(0) - 65 + i - 1) % studentNames.length;
          const studentId = `${level}${section}${i.toString().padStart(2, '0')}`;
          
          students.push({
            id: `student_${studentId}`,
            student_name: studentNames[nameIndex],
            student_id: studentId,
            class_id: classId,
            tenant_id: 'tn_01',
            elective_subjects: level >= 6 ? ['生物', '化學'] : null,
            saas_classes: {
              id: classId,
              class_name: className,
              class_level: level,
              class_section: section
            }
          });
        }
      });
    });
    
    return students;
  }
  
  /**
   * Generate mock teacher data
   * @returns {Array} Array of mock teacher objects
   */
  generateMockTeachers() {
    const teachers = [
      { id: 'teacher_001', teacher_name: '陳嘉俊', teacher_id: 'T001', subjects: ['未指定'], role: '教師', tenant_id: 'tn_01' },
      { id: 'teacher_002', teacher_name: '陳廣隆', teacher_id: 'T002', subjects: ['中文科'], role: 'Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_003', teacher_name: '陳柏輝', teacher_id: 'T003', subjects: ['英文科'], role: 'Vice Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_004', teacher_name: '陳雕鳳', teacher_id: 'T004', subjects: ['體育科'], role: 'Vice Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_005', teacher_name: '陳偉光', teacher_id: 'T005', subjects: ['未指定'], role: '教師', tenant_id: 'tn_01' },
      { id: 'teacher_006', teacher_name: '鄒志良', teacher_id: 'T006', subjects: ['資訊科技科'], role: 'Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_007', teacher_name: '鄒永新', teacher_id: 'T007', subjects: ['生物科'], role: 'Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_008', teacher_name: '陳凱怡', teacher_id: 'T008', subjects: ['視覺藝術科'], role: 'Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_009', teacher_name: '鄭振壽', teacher_id: 'T009', subjects: ['數學科'], role: 'Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_010', teacher_name: '鄭明忠', teacher_id: 'T010', subjects: ['設計與應用科技科'], role: 'Vice Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_011', teacher_name: '張煥森', teacher_id: 'T011', subjects: ['未指定'], role: '教師', tenant_id: 'tn_01' },
      { id: 'teacher_012', teacher_name: '蔡美玉', teacher_id: 'T012', subjects: ['化學科'], role: 'Vice Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_013', teacher_name: '徐隆堅', teacher_id: 'T013', subjects: ['數學科'], role: 'Class Master', tenant_id: 'tn_01' },
      { id: 'teacher_014', teacher_name: '徐文超', teacher_id: 'T014', subjects: ['未指定'], role: '教師', tenant_id: 'tn_01' },
      { id: 'teacher_015', teacher_name: '鍾麗綺', teacher_id: 'T015', subjects: ['會計科'], role: 'Vice Class Master', tenant_id: 'tn_01' }
    ];
    
    return teachers.map(teacher => ({
      ...teacher,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  }
}

// Create and export service instance
export const timetableService = new TimetableService();
export { TimetableService };

// Default export
export default timetableService;
